/** A modal: focus trapped while open, Escape to close, focus back to the trigger. */

import { useEffect, useId, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";

import { useFocusTrap } from "../internal/useFocusTrap";
import { lengthOf, mergeStyle } from "../internal/style";
import "./Dialog.css";

export interface DialogProps {
  readonly open: boolean;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  /** Escape and the backdrop stop closing it. */
  readonly persistent?: boolean;
  readonly width?: string | number;
  readonly onClose?: () => void;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode;
}

export function Dialog({
  open,
  title,
  description,
  actions,
  persistent = false,
  width,
  onClose,
  className,
  style,
  children,
}: DialogProps) {
  const base = useId();
  const surface = useFocusTrap<HTMLDivElement>(open, persistent ? undefined : onClose);

  useEffect(() => {
    if (!open) return;
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = held;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="al-dialog-scrim" onMouseDown={persistent ? undefined : onClose}>
      <div
        ref={surface}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${base}-title`}
        aria-describedby={description === undefined ? undefined : `${base}-description`}
        tabIndex={-1}
        className={cn("al-dialog", className)}
        style={mergeStyle(style, { maxWidth: lengthOf(width) })}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="al-dialog-title" id={`${base}-title`}>
          {title}
        </h2>
        {description !== undefined ? (
          <div className="al-dialog-description" id={`${base}-description`}>
            {description}
          </div>
        ) : null}
        {children !== undefined ? <div className="al-dialog-body">{children}</div> : null}
        {actions !== undefined ? <div className="al-dialog-actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
